import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { FortuneWheelApi } from '@/api/cms';
import {
  type Lootbox,
  type LootboxItem,
  LootboxStage,
  playerLootboxActivation,
} from '@/api/common';
import { ImageComponent } from '@/components/Image';
import { delay } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { useHandleBonus } from '@/hooks/bonuses';
import { useTimeoutRegistrationModal } from '@/hooks/timeoutRegistrationModal';
import { ScheduleStatus } from '@/modules/common';
import { formatLootboxItemTitle } from '@/modules/promotions/helpers/fortuneWheel';
import { getMockLootBox } from '@/modules/promotions/helpers/fortuneWheelLootbox';
import { useFortuneWheel } from '@/modules/promotions/hooks/useFortuneWheel';
import { useModalsStore } from '@/store';
import { useGetCommonStore } from '@/store/commonStore';
import { WheelStatusEnum } from '../../../../helpers/fortuneWheel';
import { PrizeWheel } from '../PrizeWheel';
import { PrizeComp } from './components/Prize';
import { WheelButton } from './components/WheelButton';
import { ScWheelArrow, ScWheelContainer, ScWheelPrizes } from './styled';

const WHEEL_ARRANGEMENT_4 = [0, 1, 2, 0, 3, 1, 0, 2, 3];
const WHEEL_ARRANGEMENT_5 = [0, 1, 2, 3, 4, 0, 1, 2, 3];

interface WheelProps {
  lootboxes: Lootbox[];
  arrowImg: string;
  bonusBuyPromocode?: string;
  wheelImages: FortuneWheelApi['wheel'];
  mode: ScheduleStatus;
}

export const Wheel: FC<WheelProps> = ({
  lootboxes,
  arrowImg,
  wheelImages,
  bonusBuyPromocode,
  mode,
}) => {
  const { t } = useTranslation('wheel');

  const useCommonStore = useGetCommonStore();

  const {
    fetchLootboxes,
    isAuthenticated,
    user,
    pendingUser,
    pendingLootboxes,
  } = useCommonStore(
    useShallow((state) => ({
      fetchLootboxes: state.fetchLootboxes,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      pendingUser: state.pendingUser,
      pendingLootboxes: state.pendingLootboxes,
    }))
  );
  const open = useModalsStore.use.open();

  const code = user && bonusBuyPromocode ? bonusBuyPromocode : null;

  const { handleBonus, pendingBonus } = useHandleBonus(code);

  const [winningPrizeIndex, setWinningPrizeIndex] = useState(0);
  const [lootboxItem, setLootboxItem] = useState<LootboxItem | null>(null);
  const [status, setStatus] = useState<WheelStatusEnum | null>(null);

  const { isDailySpinsLimit } = useFortuneWheel();

  const mockLootbox = useMemo(() => getMockLootBox(t), [t]);

  useEffect(() => {
    setWinningPrizeIndex(0);
    setLootboxItem(null);
    setStatus(null);
  }, [isAuthenticated]);

  const lootbox = useMemo(
    () =>
      (isAuthenticated &&
        lootboxes.find((lootbox) => lootbox.stage === LootboxStage.Issued)) ||
      null,
    [lootboxes, isAuthenticated]
  );

  const baseItems = (lootbox || mockLootbox).items;

  const wheelArrangement =
    baseItems.length >= 5 ? WHEEL_ARRANGEMENT_5 : WHEEL_ARRANGEMENT_4;

  const displayItems = useMemo(
    () =>
      wheelArrangement.map((itemIdx, i) => {
        const item = baseItems[itemIdx];
        return {
          key: `${item.id}-${i}`,
          displayTitle: formatLootboxItemTitle(item.bonuses[0].title),
        };
      }),
    [baseItems, wheelArrangement]
  );

  const handleSpin = useCallback(async () => {
    setStatus(WheelStatusEnum.Loading);
    const { data } = await playerLootboxActivation(lootbox!.id);

    const realItemIndex =
      lootbox?.items.findIndex((item) => item.id === data?.id) ?? -1;
    const winningIndex = wheelArrangement.indexOf(realItemIndex);

    setWinningPrizeIndex(winningIndex);
    setStatus(WheelStatusEnum.Spinning);

    await delay(7000);
    setLootboxItem(data!);

    await delay(2000);
    setStatus(WheelStatusEnum.Finished);

    fetchLootboxes();
  }, [lootbox, fetchLootboxes, wheelArrangement]);

  const handleClickWheelButton = useCallback(async () => {
    if (!isAuthenticated) {
      open(Modals.Registration);
      return;
    }

    if (!lootbox) {
      handleBonus();
      return;
    }

    await handleSpin();
  }, [isAuthenticated, lootbox, handleSpin, open, handleBonus]);

  useTimeoutRegistrationModal();

  const wheelImageByStatus = useMemo(() => {
    if (status === WheelStatusEnum.Finished) return wheelImages?.finished?.url;
    if (status === WheelStatusEnum.Spinning) return wheelImages?.spinning?.url;
    if (lootbox) return wheelImages?.active?.url;

    return wheelImages?.finished?.url;
  }, [status, wheelImages, lootbox]);

  const isSpinning = status === WheelStatusEnum.Spinning;

  const isComingSoon = mode === ScheduleStatus.COMING_SOON;

  return (
    <ScWheelContainer>
      <div className="inner">
        {wheelImageByStatus && (
          <ImageComponent
            alt="wheel image"
            className="wheelImage"
            height={512}
            src={wheelImageByStatus}
            width={512}
          />
        )}
        {status === WheelStatusEnum.Finished && lootboxItem ? (
          <PrizeWheel lootboxItem={lootboxItem} />
        ) : (
          <>
            <ScWheelPrizes
              isSpinning={isSpinning}
              totalPrizes={displayItems.length}
              winningPrizeIndex={winningPrizeIndex}
            >
              {displayItems.map((item, index) => (
                <PrizeComp
                  displayTitle={item.displayTitle}
                  index={index}
                  isHighlighted={!!lootboxItem && index === winningPrizeIndex}
                  isLocked={!lootbox}
                  key={item.key}
                  total={displayItems.length}
                />
              ))}
            </ScWheelPrizes>
            <WheelButton
              invert={!lootbox}
              isComingSoon={isComingSoon}
              isLoading={
                status === WheelStatusEnum.Loading ||
                pendingBonus ||
                pendingUser ||
                pendingLootboxes
              }
              isLocked={!isSpinning && isDailySpinsLimit}
              isSpinning={isSpinning || isDailySpinsLimit}
              onClick={handleClickWheelButton}
            />
            <ScWheelArrow arrowImg={arrowImg} />
          </>
        )}
      </div>
    </ScWheelContainer>
  );
};
